import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
} from "react";

interface ConfirmContextType {
  showConfirm: (
    text: string,
    confirmText: string,
    cancelText: string,
    confirmCallback: () => void,
    cancelCallback?: () => void,
  ) => void;
  hideConfirm: () => void;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

interface ConfirmProviderProps {
  children: ReactNode;
}

export const ConfirmProvider: React.FC<ConfirmProviderProps> = ({
  children,
}) => {
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const showConfirm = useCallback(
    (
      text: string,
      confirmText: string,
      cancelText: string,
      confirmCallback: () => void,
      cancelCallback?: () => void,
    ) => {
      const id = Math.random().toString(36).substr(2, 9);
      setConfirm({
        text,
        confirmText,
        cancelText,
        confirmCallback,
        cancelCallback,
        isVisible: true,
        id,
      });
      setShouldRender(true);
      setTimeout(() => setIsAnimating(true), 10);
    },
    [],
  );

  const hideConfirm = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => {
      setShouldRender(false);
      setConfirm(null);
    }, 300);
  }, []);

  const handleConfirm = useCallback(() => {
    if (confirm?.confirmCallback) {
      confirm.confirmCallback();
    }
    hideConfirm();
  }, [confirm, hideConfirm]);

  const handleCancel = useCallback(() => {
    if (confirm?.cancelCallback) {
      confirm.cancelCallback();
    }
    hideConfirm();
  }, [confirm, hideConfirm]);

  const contextValue: ConfirmContextType = {
    showConfirm,
    hideConfirm,
  };

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}
      {shouldRender && confirm && (
        <>
          {/* Backdrop */}
          <div
            className={`
              fixed inset-0 bg-black z-40
              transition-opacity duration-300 ease-in-out
              ${isAnimating ? "opacity-50" : "opacity-0"}
            `}
            onClick={handleCancel}
          />

          {/* Modal */}
          <div className="fixed inset-x-0 bottom-[76px] z-50 flex items-end justify-center pointer-events-none">
            <div
              className={`
                bg-white rounded-4 shadow-xl
                max-w-[500px] w-full pointer-events-auto
                transform transition-all duration-300 ease-in-out
                ${
                  isAnimating
                    ? "translate-y-0 opacity-100 scale-100"
                    : "translate-y-8 opacity-0 scale-95"
                }
              `}
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-dialog-title"
            >
              {/* Modal Content */}
              <div className="py-3.5 pl-3 pr-5">
                {/* Message */}
                <div className="mb-3">
                  <p
                    id="confirm-dialog-title"
                    className="text-center text-sm text-bet-builder-modal-keep-text"
                  >
                    {confirm.text}
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex flex-row gap-2">
                  {/* Confirm Button */}
                  <button
                    onClick={handleConfirm}
                    className="flex-1 bg-btn-bet-builder-keep text-btn-bet-builder-keep-text font-semibold text-sm h-[36px] py-1 px-4 rounded-4 focus:outline-none"
                  >
                    {confirm.confirmText}
                  </button>

                  {/* Cancel Button */}
                  <button
                    onClick={handleCancel}
                    className="flex-1 bg-transparent border border-btn-bet-builder-switch-border text-btn-bet-builder-switch-text font-semibold text-sm h-[36px] py-1 px-4 rounded-4 focus:outline-none"
                  >
                    {confirm.cancelText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextType => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context;
};
